import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Country } from '@zero/onboarding/domain';
import { IconComponent, InputComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, take, tap } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';
import { CountryFilterPipe } from '../../pipes/country/country-filter.pipe';
import { PhonePipe } from '../../pipes/validation/phone/phone.pipe';
import { VerificationCodePipe } from '../../pipes/validation/verification-code/verification-code.pipe';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    InputComponent,
    NgbDropdownModule,
    AsyncPipe,
    IconComponent,
    NgTemplateOutlet,
    PhonePipe,
    VerificationCodePipe,
    FormsModule,
    CountryFilterPipe,
  ],
  templateUrl: './mobile-number.component.html',
  styleUrl: './mobile-number.component.scss',
})
export class MobileNumberComponent extends BaseComponent implements OnInit {
  readonly #step = new BehaviorSubject<Step | undefined>(undefined);
  readonly #mobileNumber = new BehaviorSubject<string>('');
  readonly #code = new BehaviorSubject<string>('');
  readonly #activeCountry = new BehaviorSubject<Country>(undefined);
  readonly #destroyRef = inject(DestroyRef);

  public readonly Step = Step;
  public readonly mobileNumber$ = this.#mobileNumber.asObservable();
  public readonly code$ = this.#code.asObservable();
  public readonly step$ = this.#step.asObservable();
  public readonly activeCountry$ = this.#activeCountry.asObservable();
  public countryFilter = '';

  public isResendEnabled = true;
  public hasResendCodeError = false;
  public resendEnabledCountdown: number;
  public onHoverResend = false;

  public readonly formValid$ = combineLatest([
    this.step$,
    this.mobileNumber$,
    this.code$,
    this.activeCountry$,
    this.facade.sendingPhoneNumber$,
    this.facade.verifyingCode$,
  ]).pipe(
    map(([step, mobileNumber, code, activeCountry, sendingPhoneNumber, verifyingCode]) => {
      if (sendingPhoneNumber || verifyingCode) {
        return false;
      } else if (step === Step.NUMBER_VERIFIED) {
        return true;
      } else if (step === Step.ADD_NUMBER) {
        return new PhonePipe().transform(mobileNumber, activeCountry.phoneCode);
      } else if (step === Step.VERIFY_NUMBER) {
        return new VerificationCodePipe().transform(code);
      } else {
        return false;
      }
    }),
  );

  public override ngOnInit(): void {
    super.ngOnInit();
    this.#initStepByData();
    this.#listenToCodeResendStarted();
    this.#listenToResendCodeError();
  }

  protected override submit(): Promise<void> | void {
    this.goForward();
  }

  protected override populateFromData(): void {
    combineLatest([this.facade.onboardingData$, this.facade.countries$])
      .pipe(
        filter(([onboardingData, countries]) => Boolean(onboardingData && countries?.length)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(([{ cMobileNr }, countries]) => {
        const country = countries.find((country) => cMobileNr?.startsWith(country.phoneCode));
        this.#activeCountry.next(country || countries[0]);
        this.#mobileNumber.next(cMobileNr ? cMobileNr.replace(country?.phoneCode, '') : '');
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.facade.sentCodeSuccessfully$
      .pipe(
        filter((sent) => !!sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }

  public onMobileNumberChange = (value: string) => this.#mobileNumber.next(value);
  public onCodeChange = (value: string) => {
    this.facade.resetSentCodeSuccessfully();
    this.#code.next(value);
  };
  public selectCountry = (country: Country) => this.#activeCountry.next(country);

  public goForward() {
    const step = this.#step.value;
    if (step === Step.NUMBER_VERIFIED) {
      this.facade.goToNextStep();
    } else if (this.#step.value === Step.ADD_NUMBER) {
      this.facade.sendPhoneNumber(this.#activeCountry.value.phoneCode + this.#mobileNumber.value);
      this.facade.sentPhoneNumberSuccessfully$
        .pipe(
          filter((sent) => !!sent),
          take(1),
        )
        .subscribe(() => this.#step.next(Step.VERIFY_NUMBER));
    } else {
      this.facade.verifyCode(this.#code.value);
    }
  }

  public goBack() {
    if (this.#step.value === Step.VERIFY_NUMBER) {
      this.#step.next(Step.ADD_NUMBER);
      this.#mobileNumber.next('');
    }
  }

  #listenToCodeResendStarted(): void {
    this.facade.resendCodeInProgress$
      .pipe(
        filter((resendCodeInProgress) => resendCodeInProgress),
        tap(() => (this.isResendEnabled = false)),
        tap(() => this.#startResendCountdown()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#code.next(''));
  }

  #listenToResendCodeError(): void {
    this.facade.resendCodeError$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((error) => (this.hasResendCodeError = !!error));
  }

  #startResendCountdown(): void {
    this.resendEnabledCountdown = 30;
    const intervalId = setInterval(() => {
      this.resendEnabledCountdown--;
      if (!this.resendEnabledCountdown) {
        this.isResendEnabled = true;
        clearInterval(intervalId);
      }
    }, 1000);
  }

  #initStepByData() {
    combineLatest([
      this.facade.isPhoneNumberVerified$,
      this.facade.onboardingDataLoading$,
      this.facade.onboardingData$,
      this.facade.countries$,
      this.facade.isPhoneNumberVerifiedLoading$,
    ])
      .pipe(
        filter(
          ([, onboardingDataLoading, onboardingData, countries, isPhoneNumberVerifiedLoading]) =>
            !onboardingDataLoading && !!countries && (!isPhoneNumberVerifiedLoading || !onboardingData?.cMobileNr),
        ),
        map(([isPhoneNumberVerified, , onboardingData]) => [isPhoneNumberVerified, !!onboardingData?.cMobileNr]),
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged(),
      )
      .subscribe(([isPhoneNumberVerified, isMobileNumberSaved]) => {
        let nextStep = Step.ADD_NUMBER;
        if (isPhoneNumberVerified) {
          nextStep = Step.NUMBER_VERIFIED;
        } else if (isMobileNumberSaved) {
          nextStep = Step.VERIFY_NUMBER;
        }

        this.#step.next(nextStep);
      });
  }
}

enum Step {
  ADD_NUMBER,
  VERIFY_NUMBER,
  NUMBER_VERIFIED,
}
